/**
 * Who is in my household, and what state the pairing is actually in.
 *
 * Reads the roster off the household doc (see householdService.Household.members
 * for why it lives there). Household size is capped at 2, so "the partner" is
 * unambiguous: the one member who is not me.
 *
 * `status` is the thing the UI was missing. Settings used to show "Household
 * paired" whenever a householdId existed — which it did from the moment the
 * Share screen was opened, before anyone had been invited, let alone joined.
 * Three states, distinguished honestly:
 *
 *   solo    — no household, or one that nobody has been invited to
 *   pending — an invitation is outstanding and still live
 *   paired  — someone actually joined
 *
 * The subscription is live so `pending` becomes `paired` the moment the
 * partner joins, rather than at the next reload.
 */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import {
  subscribeToHousehold,
  initialOf,
  initialOfDisambiguated,
  nameFor,
  isInviteLive,
  householdStatusOf,
  type HouseholdRoster,
  type PendingInvite,
  type HouseholdStatus,
} from '../services/householdService';
import { isFirebaseEnabled } from '../services/firebaseConfig';

export interface HouseholdMembers {
  status: HouseholdStatus;
  loading: boolean;
  householdId: string | null;
  myUid: string | null;
  myName: string | null;
  myInitial: string | null;
  partnerUid: string | null;
  partnerName: string | null;
  partnerEmail: string | null;
  partnerInitial: string | null;
  pendingInvite: PendingInvite | null;
  roster: HouseholdRoster | undefined;
}

const EMPTY: HouseholdMembers = {
  status: 'solo',
  loading: false,
  householdId: null,
  myUid: null,
  myName: null,
  myInitial: null,
  partnerUid: null,
  partnerName: null,
  partnerEmail: null,
  partnerInitial: null,
  pendingInvite: null,
  roster: undefined,
};

export const useHouseholdMembers = (): HouseholdMembers => {
  const myUid = useSelector((s: RootState) => s.auth?.user?.uid ?? null);
  const householdId = useSelector((s: RootState) => s.auth?.householdId ?? null);
  const [members, setMembers] = useState<HouseholdMembers>(EMPTY);

  useEffect(() => {
    if (!isFirebaseEnabled() || !myUid || !householdId) {
      setMembers({ ...EMPTY, myUid });
      return;
    }

    setMembers((prev) => ({ ...prev, loading: true, myUid, householdId }));

    return subscribeToHousehold(householdId, (hh) => {
      if (!hh) {
        setMembers({ ...EMPTY, myUid });
        return;
      }

      const partnerUid = (hh.memberIds ?? []).find((id) => id !== myUid) ?? null;
      const partnerEntry = partnerUid ? hh.members?.[partnerUid] : undefined;
      const partnerName = nameFor(partnerEntry);
      const myName = nameFor(hh.members?.[myUid]);
      const pendingInvite = isInviteLive(hh.pendingInvite)
        ? hh.pendingInvite ?? null
        : null;

      // Use disambiguated initials when both members are present
      let myInitial: string | null = null;
      let partnerInitial: string | null = null;
      if (partnerUid && myName && partnerName) {
        const [mine, theirs] = initialOfDisambiguated(myName, partnerName);
        myInitial = mine;
        partnerInitial = theirs;
      } else {
        myInitial = initialOf(myName);
        partnerInitial = partnerUid ? initialOf(partnerName) : null;
      }

      setMembers({
        status: householdStatusOf(hh, myUid),
        loading: false,
        householdId,
        myUid,
        myName,
        myInitial,
        partnerUid,
        partnerName,
        partnerEmail: partnerEntry?.email ?? null,
        partnerInitial,
        pendingInvite,
        roster: hh.members,
      });
    });
  }, [myUid, householdId]);

  return members;
};
